/**
 * Research detail page — `/cases/:caseId/investigate/search/:hypothesisId`.
 *
 * State-driven layout (DEEP_RESEARCH_REDESIGN.md — single research mode):
 *  • Running  → live RunningProgressPanel, then the statement
 *  • Finished → statement paired with the report summary, then findings, the
 *               full report, and its sources — every block a card `Section`
 *               for one consistent hierarchy (no tabs/sheet on the page). The
 *               agent chat card keeps the tabbed ResearchRunView sheet.
 */

import { ArrowLeft, Loader2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ReportMarkdown } from '@/components/research/ReportMarkdown'
import { EvidenceCard } from '@/components/search/detail/EvidenceCard'
import { FindingsList } from '@/components/search/detail/FindingsList'
import {
  HypothesisBuilderModal,
  RunningProgressPanel,
  SaveResearchReportAction,
  useHypothesis,
  useHypothesisFindings,
  useHypothesisRuns,
} from '@/features/hypotheses'
import { useFlashIndex } from '@/hooks/use-flash-index'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { type HypothesisRun, isRunFinished } from '@/types/hypothesis'
import { DetailHeader } from './detail/DetailHeader'
import { ReportSummary } from './detail/ReportSummary'
import { Section } from './detail/Section'
import { StatementSection } from './detail/StatementSection'

export default function SearchDetailPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const { hypothesisId } = useParams<{ hypothesisId: string }>()
  const navigate = useNavigate()
  const { data: hypothesis, isLoading } = useHypothesis(caseId, hypothesisId)
  const { data: runs } = useHypothesisRuns(caseId, hypothesisId)
  const [editOpen, setEditOpen] = useState(false)
  const reportRef = useRef<HTMLDivElement>(null)
  // 0-based index of the finding / source a report citation last targeted —
  // the relevant section card scrolls to it and flashes, then auto-clears.
  const [findingFlash, setFindingFlash] = useFlashIndex()
  const [sourceFlash, setSourceFlash] = useFlashIndex()

  const latestRun: HypothesisRun | undefined = runs?.[0]
  const { data: findings } = useHypothesisFindings(caseId, hypothesisId, latestRun?.id)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground md:p-6">
        <Loader2 className="size-4 animate-spin" />
        {t('search.detail.loading')}
      </div>
    )
  }

  if (!hypothesis) {
    return (
      <div className="flex flex-col gap-3 p-4 md:p-6">
        <p className="text-sm text-muted-foreground">{t('search.detail.notFound')}</p>
      </div>
    )
  }

  const isRunning = hypothesis.status === 'running'
  const hasRunResults = !isRunning && !!latestRun && isRunFinished(latestRun.status)
  const hasReport = hasRunResults && !!(latestRun.reportMd || latestRun.summary)
  const findingCount = findings?.length ?? 0
  const sources = latestRun?.sources ?? []

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate(`/cases/${caseId}/search`)}
            className="inline-flex w-fit items-center gap-1.5 text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground/80 transition-colors hover:text-foreground max-md:-my-1.5 max-md:py-1.5"
          >
            <ArrowLeft className="size-3" />
            {t('research.list.backToResearch')}
          </button>

          <DetailHeader
            title={hypothesis.title}
            runStatus={latestRun?.status}
            running={isRunning}
            onEdit={() => setEditOpen(true)}
          >
            {hasReport && <SaveResearchReportAction caseId={caseId} run={latestRun} />}
          </DetailHeader>
        </div>

        {/* Execution — the live plan, shown only while the run is processing
          (above the statement). Hidden once finished; status lives in the
          header badge. */}
        {isRunning && latestRun && hypothesisId && (
          <RunningProgressPanel
            caseId={caseId}
            hypothesisId={hypothesisId}
            runId={latestRun.id}
            startedAt={latestRun.startedAt}
          />
        )}

        {/* The assignment paired with the report's at-a-glance summary
          (equal-height cards via grid stretch). */}
        {hasReport ? (
          <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2">
            <StatementSection hypothesis={hypothesis} />
            <ReportSummary
              summary={latestRun.summary}
              onReadFull={() => reportRef.current?.scrollIntoView({ behavior: 'smooth' })}
            />
          </div>
        ) : (
          <StatementSection hypothesis={hypothesis} />
        )}

        {hasRunResults && (
          <FindingsList
            findings={findings}
            latestRun={latestRun}
            caseId={caseId}
            highlightIndex={findingFlash}
          />
        )}

        {/* Full report (markdown only — sources are their own card below, so the
          report isn't a card nested inside a card). [F#]/[S#] citations scroll
          to and flash the cited finding / source. */}
        {hasReport && (
          <div ref={reportRef}>
            <Section title={t('researchRun.tabs.report')} hint={t('search.detail.report.hint')}>
              {latestRun.reportMd ? (
                <ReportMarkdown
                  markdown={latestRun.reportMd}
                  findingCount={findingCount}
                  sourceCount={sources.length}
                  onOpenFinding={i => setFindingFlash(i - 1)}
                  onOpenSource={i => setSourceFlash(i - 1)}
                />
              ) : latestRun.summary ? (
                <p className="whitespace-pre-line break-words text-sm leading-relaxed text-foreground">
                  {latestRun.summary}
                </p>
              ) : (
                <p className="text-sm italic text-muted-foreground">{t('researchRun.noReport')}</p>
              )}
            </Section>
          </div>
        )}

        {hasRunResults && sources.length > 0 && (
          <EvidenceCard evidence={sources} highlightIndex={sourceFlash} />
        )}

        <HypothesisBuilderModal
          open={editOpen}
          onOpenChange={setEditOpen}
          caseId={caseId}
          hypothesis={hypothesis}
        />
      </div>
    </div>
  )
}
